/**
 * §9 規則 7：顯示名稱一律讀料號字典，不在元件裡寫死中文。
 *
 * mockup 的 `useDict()` 從 `config` context 讀；正式站台的設定來自
 * `GET /api/config`（§12.2 已經合併好全公司與本廠區兩層）。
 * 介面刻意與 mockup 的 `useDict()` 一模一樣，搬畫面時不用改呼叫端。
 */

import React, { createContext, useContext, useMemo } from 'react'
import type { Config } from '../../../shared/types/config.js'
import { useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'

interface ConfigCtx {
  config: Config | null
  loading: boolean
  error: Error | null
  reload: () => void
}

const Ctx = createContext<ConfigCtx | null>(null)

interface ConfigResponse {
  siteId: string
  config: Partial<Config>
  scopes: { global: string[]; site: string[] }
}

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const { me, canAny } = useAuth()
  // §8.5：settings.read 或任何作業權限都能讀設定
  const allowed = canAny(
    'settings.read', 'receiving.register', 'receipt.post', 'issue.post',
    'inventory.read', 'panel.read', 'balance.read',
  )
  const path = me?.site && allowed ? '/api/config' : null
  const { data, loading, error, reload } = useQuery<ConfigResponse>(path, [me?.site?.siteId])

  const value = useMemo<ConfigCtx>(() => ({
    config: (data?.config as Config) ?? null,
    loading,
    error,
    reload,
  }), [data, loading, error, reload])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useConfig(): ConfigCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useConfig 必須在 ConfigProvider 裡使用')
  return c
}

/**
 * §9 規則 7 的實作。查不到就退回代碼——**不要**回「未知」那種字串，
 * 代碼本身就是有用的資訊（可以拿去問人、可以搜尋）。
 */
export function useDict() {
  const { config } = useConfig()
  return useMemo(() => {
    const c = config
    return {
      material: (code: string | null) =>
        (code && c?.materialDict?.find((m) => m.materialCode === code)?.label) ?? code ?? '—',
      materialShort: (code: string | null) =>
        (code && c?.materialDict?.find((m) => m.materialCode === code)?.shortLabel) ?? code ?? '—',
      station: (id: string | null) =>
        (id && c?.stations?.find((s) => s.stationId === id)?.label) ?? id ?? '—',
      resource: (id: string | null) =>
        (id && c?.resources?.find((r) => r.resourceId === id)?.label) ?? id ?? '—',
      container: (id: string | null) =>
        (id && c?.containers?.find((x) => x.containerId === id)?.label) ?? id ?? '—',
      location: (id: string | null) =>
        (id && c?.locations?.find((l) => l.locationId === id)?.label) ?? id ?? '—',
      scale: (id: string | null) =>
        (id && c?.scales?.find((s) => s.scaleId === id)?.label) ?? id ?? '—',
      measurementPoint: (id: string | null) =>
        c?.measurementPoints?.find((m) => m.measurementPointId === id) ?? null,
      term: (key: string) => c?.terms?.[key] ?? key,
    }
  }, [config])
}
